import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AsyncSelect from 'react-select/async';
import useUsers from "../../../hooks/useUsers";
import { handleLoadSelectOptions, mapValues } from "../../../util/Utilities";
import useServices from "../../../hooks/useServices";
import update from 'immutability-helper';
import useAxios from "../../../hooks/useAxios";
import { useFeedBack } from "../../../context/FeedBackContext";
import usePositions from "../../../hooks/usePositions";

const EppOrdersCreate = () => {

    const navigate = useNavigate();

    const { setCustomAlert } = useFeedBack();

    const [{ users, loading: usersLoading }, getUsers] = useUsers({ params: { perPage: 200 } }, { useCache: false });

    const [{ services, loading: servicesLoading }, getServices] = useServices({ params: { perPage: 200 } }, { useCache: false });

    const [{ positions, loading: positionsLoading }, getPositions] = usePositions({ params: { perPage: 200 } }, { useCache: false });

    const [{ data: storeData, loading: loadingStore }, storeRecord] = useAxios({ url: `/epp-orders`, method: 'POST' }, { manual: true, useCache: false });

    const [selectedUserOption, setSelectedUserOption] = useState(null);

    const [data, setData] = useState({
        reason: 'nuevo_ingreso',
        user_name: '',
        user_document_number: '',
        user_phone_number: '',
        from_service: null,
        service: null,
        position: null,
        entry_date: '',
        observations: '',
        order_items: [
            {
                description: 'Camisa',
                value: ''
            },
            {
                description: 'Chaleco Geologo',
                value: ''
            },
            {
                description: 'Pantalon',
                value: ''
            },
            {
                description: 'Zapatos de seguridad',
                value: ''
            },
            {
                description: 'Chaqueta',
                value: ''
            },
            {
                description: 'Usa lentes opticos permanentes para trabajar',
                value: 'si'
            },
        ]
    });

    useEffect(() => {
        if (storeData) {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: `El registro fue creado exitosamente.`,
                show: true
            });
            navigate?.(`/solicitudes-de-epp`);
        }
    }, [storeData]);

    const handleChange = (e) => {
        setData((oldData) => {
            return {
                ...oldData,
                [e.target.name]: e.target.value
            }
        });
    };

    const handleArrayChange = (e, index, arrayName) => {
        const newArrayValues = update(data?.[arrayName], { [index]: { [e.target.name]: { $set: e.target.value } } });
        setData((oldData) => {
            return {
                ...oldData,
                [arrayName]: newArrayValues
            }
        });
    };

    const loadUserOptions = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];
        const res = await getUsers({ params: { search: inputValue, perPage: 50 } }, { useCache: false });
        const userList = res?.data?.data || [];
        return userList.map((u) => ({
            label: `${u.documentNumber || '--'} - ${u.name}`,
            value: u.id,
            user: u
        }));
    };

    const handleUserSelect = (option) => {
        setSelectedUserOption(option);
        if (option && option.user) {
            const u = option.user;
            setData((oldData) => ({
                ...oldData,
                user_name: u.name || '',
                user_document_number: u.documentNumber || '',
                user_phone_number: u.phoneNumber || '',
                position: u.position ? { label: u.position.name, value: u.position.id } : oldData.position
            }));
        }
    };

    const handleSubmit = () => {
        storeRecord({
            data: {
                reason: data?.reason,
                user_name: data?.user_name,
                user_document_number: data?.user_document_number,
                user_phone_number: data?.user_phone_number,
                from_service_id: data?.reason === 'traslado' ? data?.from_service?.value : null,
                service_id: data?.service?.value,
                position_id: data?.position?.value,
                entry_date: data?.entry_date,
                observations: data?.observations,
                order_items: data?.order_items
            }
        });
    };

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex align-items-center w-100">
                    <div>
                        <h5 className="card-title">
                            Crear Solicitud de EPP
                        </h5>
                    </div>
                    <div style={{ marginLeft: 'auto' }} className="text-end">
                        <button className="btn btn-xs btn-primary" onClick={() => navigate(-1)}>
                            Volver
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row">

                    {/* Motivo de la Solicitud */}
                    <div className="col-md-12 mb-3">
                        <label className="text-primary fw-bold mb-2">
                            Motivo de la solicitud
                        </label>
                        <div className="d-flex gap-4">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="reason"
                                    id="reason_nuevo"
                                    value="nuevo_ingreso"
                                    checked={data?.reason === 'nuevo_ingreso'}
                                    onChange={handleChange}
                                />
                                <label className="form-check-label fw-bold cursor-pointer" htmlFor="reason_nuevo">
                                    Nuevo ingreso
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="reason"
                                    id="reason_traslado"
                                    value="traslado"
                                    checked={data?.reason === 'traslado'}
                                    onChange={handleChange}
                                />
                                <label className="form-check-label fw-bold cursor-pointer" htmlFor="reason_traslado">
                                    Traslado
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Sugerencias de Usuario por RUT / Nombre */}
                    <div className="col-md-12 mb-3">
                        <div className="form-group">
                            <label className="text-primary">
                                Buscar trabajador por RUT o Nombre (Sugerencias)
                            </label>
                            <AsyncSelect
                                isClearable
                                cacheOptions
                                value={selectedUserOption}
                                loadOptions={loadUserOptions}
                                placeholder="Escriba el RUT o nombre del trabajador..."
                                onChange={handleUserSelect}
                                noOptionsMessage={() => "Escriba al menos 2 caracteres para buscar"}
                            />
                            <small className="text-muted">
                                Al seleccionar un usuario se autocompletarán los campos RUT, Nombre, Teléfono y Cargo.
                            </small>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="text-primary">
                                RUT del trabajador
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                name="user_document_number"
                                value={data?.user_document_number}
                                onChange={handleChange}
                                placeholder="Ingrese el rut del trabajador..."
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="text-primary">
                                Nombre del trabajador
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                name="user_name"
                                value={data?.user_name}
                                onChange={handleChange}
                                placeholder="Ingrese el nombre del trabajador..."
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="text-primary">
                                Teléfono del trabajador
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                name="user_phone_number"
                                value={data?.user_phone_number}
                                onChange={handleChange}
                                placeholder="Ingrese el teléfono del trabajador..."
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="text-primary">Seleccione el cargo</label>
                            <AsyncSelect
                                isClearable
                                onFocus={() => { getPositions() }}
                                value={data?.position}
                                isLoading={positionsLoading}
                                defaultOptions={mapValues(positions)}
                                name="position"
                                loadOptions={(e) => handleLoadSelectOptions(e, getPositions)}
                                placeholder='Escriba el nombre para buscar...'
                                onChange={(e) => { handleChange({ target: { value: e, name: 'position' } }) }}
                            />
                        </div>
                    </div>

                    {/* Servicios según motivo */}
                    {data?.reason === 'traslado' ? (
                        <>
                            <div className="col-md-6">
                                <div className="form-group mb-3">
                                    <label className="text-primary">Servicio desde</label>
                                    <AsyncSelect
                                        isClearable
                                        onFocus={() => { getServices() }}
                                        value={data?.from_service}
                                        isLoading={servicesLoading}
                                        defaultOptions={mapValues(services)}
                                        name="from_service"
                                        loadOptions={(e) => handleLoadSelectOptions(e, getServices)}
                                        placeholder='Escriba el servicio de origen...'
                                        onChange={(e) => { handleChange({ target: { value: e, name: 'from_service' } }) }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group mb-3">
                                    <label className="text-primary">Servicio hasta</label>
                                    <AsyncSelect
                                        isClearable
                                        onFocus={() => { getServices() }}
                                        value={data?.service}
                                        isLoading={servicesLoading}
                                        defaultOptions={mapValues(services)}
                                        name="service"
                                        loadOptions={(e) => handleLoadSelectOptions(e, getServices)}
                                        placeholder='Escriba el servicio de destino...'
                                        onChange={(e) => { handleChange({ target: { value: e, name: 'service' } }) }}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="col-md-6">
                            <div className="form-group mb-3">
                                <label className="text-primary">Servicio donde va a ingresar</label>
                                <AsyncSelect
                                    isClearable
                                    onFocus={() => { getServices() }}
                                    value={data?.service}
                                    isLoading={servicesLoading}
                                    defaultOptions={mapValues(services)}
                                    name="service"
                                    loadOptions={(e) => handleLoadSelectOptions(e, getServices)}
                                    placeholder='Escriba el servicio para buscar...'
                                    onChange={(e) => { handleChange({ target: { value: e, name: 'service' } }) }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label className="text-primary">
                                Fecha de ingreso del trabajador
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                name="entry_date"
                                value={data?.entry_date}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="form-group mb-3">
                            <label className="text-primary">
                                Observaciones
                            </label>
                            <textarea
                                className="form-control"
                                name="observations"
                                value={data?.observations}
                                onChange={handleChange}
                                placeholder="Ingrese observaciones o comentarios adicionales..."
                                rows={3}
                            />
                        </div>
                    </div>
                    {
                        data?.order_items?.map((orderItem, i) => {
                            return (
                                <div className="col-md-12" key={i}>
                                    <div className="input-group mb-3">
                                        <span className="input-group-text text-primary justify-content-start" id={`basic-addon-${i}`} style={{ width: 310 }}>
                                            {orderItem?.description}
                                        </span>
                                        {
                                            i !== 5 ?
                                                <input type="text"
                                                    className="form-control"
                                                    value={orderItem?.value}
                                                    placeholder="Ingrese el valor..."
                                                    name="value"
                                                    onChange={(e) => handleArrayChange(e, i, 'order_items')}
                                                />
                                                :
                                                <select
                                                    className="form-control"
                                                    name="value"
                                                    onChange={(e) => handleArrayChange(e, i, 'order_items')}
                                                >
                                                    <option value="si">Si</option>
                                                    <option value="no">No</option>
                                                </select>
                                        }
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
            </div>
            <div className="card-footer text-end justify-content-end">
                <button className="btn btn-danger" onClick={() => navigate(-1)} type="button">
                    Cancelar
                </button>
                <button className="btn btn-primary ms-2" onClick={handleSubmit} disabled={loadingStore}>
                    Enviar
                </button>
            </div>
        </div>
    );
};

export default EppOrdersCreate;